import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { Avatar } from "../components/Avatar";
import { ProposalCard } from "../components/ProposalCard";
import { StyleTag, MetaChip, Loader, EmptyState, Card } from "../components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Music, Send, Check, Share2, CheckCircle2, Trash2, Inbox, Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";

function SearchHeader({ s }) {
  return (
    <Card className="p-5 mb-5">
      <div className="flex items-center gap-3 mb-4">
        <Avatar user={s.artist} size={44} />
        <div>
          <p className="font-semibold">🎤 @{s.artist?.pseudo}</p>
          {s.artist?.artist_name && <p className="text-xs text-zinc-400">{s.artist.artist_name}</p>}
        </div>
        {s.status === "completed" && <span className="ml-auto text-xs font-mono px-2 py-1 rounded-full bg-zinc-700/60">Terminée</span>}
      </div>
      <h1 className="text-xl sm:text-2xl font-display font-bold mb-3">{s.title}</h1>
      <div className="flex flex-wrap gap-2 mb-4">{(s.styles || []).map((x) => <StyleTag key={x}>{x}</StyleTag>)}</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 mb-4">
        <MetaChip label="BPM" value={s.bpm_min ? `${s.bpm_min}${s.bpm_max ? `–${s.bpm_max}` : ""}` : "—"} />
        <MetaChip label="Tonalité" value={s.key} />
        <MetaChip label="Budget" value={s.budget} />
        <MetaChip label="Propositions" value={s.submission_count} />
      </div>
      {s.moods?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">{s.moods.map((m) => <span key={m} className="text-xs px-2 py-0.5 rounded-md bg-zinc-800 text-zinc-300">{m}</span>)}</div>
      )}
      {s.references && <p className="text-sm text-zinc-400 mb-1"><span className="text-zinc-500">Références :</span> {s.references}</p>}
      {s.description && <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{s.description}</p>}
    </Card>
  );
}

function ProductionPickRow({ prod, selected, onToggle, disabled }) {
  const { track, playing, play } = usePlayer();
  const cover = mediaUrl(prod, "image");
  const isCurrent = track && track.id === `pick-${prod.id}`;
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all ${selected ? "border-violet-500 bg-violet-600/10" : "border-zinc-800 bg-zinc-900/50"}`}>
      <button onClick={() => play({ id: `pick-${prod.id}`, title: prod.title, subtitle: prod.style || "", production: prod, cover })}
        className="w-11 h-11 rounded-lg overflow-hidden shrink-0 relative">
        {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-700 to-purple-900 flex items-center justify-center"><Music size={16} /></div>}
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          {isCurrent && playing ? <Pause size={16} fill="white" /> : <Play size={16} fill="white" className="ml-0.5" />}
        </div>
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{prod.title}</p>
        <p className="text-[11px] font-mono text-zinc-500">{[prod.style, prod.bpm && `${prod.bpm} BPM`].filter(Boolean).join(" · ")}</p>
      </div>
      <button onClick={() => onToggle(prod.id)} disabled={disabled && !selected} data-testid={`pick-${prod.id}`}
        className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all shrink-0 ${selected ? "bg-violet-600 border-violet-500 text-white" : "border-zinc-600 text-transparent disabled:opacity-30"}`}>
        <Check size={16} />
      </button>
    </div>
  );
}

export default function SearchRouter() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [prods, setProds] = useState([]);
  const [modal, setModal] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const isOwner = user?.role === "artist" && s && s.artist_id === user.id;
  const isBeatmaker = user?.role === "beatmaker";

  const load = () => {
    api.get(`/searches/${id}`).then((r) => {
      setS(r.data);
      if (user?.role === "artist" && r.data.artist_id === user.id) {
        api.get(`/searches/${id}/submissions`).then((res) => setProposals(res.data));
      }
    }).catch(() => setNotFound(true));
  };
  useEffect(load, [id]);

  const openModal = async () => {
    const { data } = await api.get("/productions/mine");
    setProds(data); setPicked([]); setModal(true);
  };

  const remaining = s ? 3 - (s.my_submission_count || 0) : 3;
  const togglePick = (pid) => setPicked((p) => p.includes(pid) ? p.filter((x) => x !== pid) : (p.length < remaining ? [...p, pid] : p));

  const submitProposals = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/searches/${id}/submissions`, { production_ids: picked });
      toast.success(`${data.created} production(s) envoyée(s) !`);
      setModal(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setReaction = (subId, type) => setProposals((p) => p.map((x) => (x.submission_id === subId || x.id === subId) ? { ...x, reaction: type } : x));

  const toggleStatus = async () => {
    const next = s.status === "active" ? "completed" : "active";
    await api.patch(`/searches/${id}/status?status=${next}`);
    toast.success(next === "completed" ? "Recherche marquée terminée" : "Recherche réactivée");
    load();
  };

  const del = async () => {
    if (!window.confirm("Supprimer cette recherche ?")) return;
    await api.delete(`/searches/${id}`);
    toast.success("Recherche supprimée"); nav("/app");
  };

  const share = () => {
    const url = `${window.location.origin}/r/${s.code}`;
    if (navigator.share) navigator.share({ url, title: s.title }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Lien copié !"); }
  };

  if (notFound) return <EmptyState icon={Inbox} title="Recherche introuvable" />;
  if (!s) return <Loader />;

  return (
    <div className="max-w-2xl mx-auto">
      <SearchHeader s={s} />

      {isBeatmaker && (
        <div className="mb-6">
          {(s.my_submission_count || 0) >= 3 ? (
            <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-sm" data-testid="limit-reached-message">
              <CheckCircle2 size={18} /> Vous avez déjà envoyé 3 productions pour cette recherche.
            </div>
          ) : (
            <button onClick={openModal} data-testid="open-propose-modal-button"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold glow-violet transition-all">
              <Send size={18} /> Proposer mes productions {s.my_submission_count > 0 && `(${s.my_submission_count}/3)`}
            </button>
          )}
        </div>
      )}

      {isOwner && (
        <>
          <div className="flex gap-2 mb-5">
            <button onClick={share} data-testid="share-search-button" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-zinc-700 hover:border-violet-600 text-sm font-medium transition-colors"><Share2 size={16} /> Partager</button>
            <button onClick={toggleStatus} data-testid="toggle-status-button" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-zinc-700 hover:border-emerald-600 text-sm font-medium transition-colors"><CheckCircle2 size={16} /> {s.status === "active" ? "Terminer" : "Réactiver"}</button>
            <button onClick={del} data-testid="delete-search-button" className="w-11 flex items-center justify-center rounded-xl border border-zinc-700 hover:border-red-600 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
          </div>
          <h2 className="text-lg font-display font-bold mb-4">Propositions reçues ({proposals.length})</h2>
          {proposals.length === 0 ? (
            <EmptyState icon={Inbox} title="Pas encore de proposition" desc="Partage ton lien pour recevoir des prods." />
          ) : (
            <div className="space-y-3">
              {proposals.map((p) => <ProposalCard key={p.submission_id || p.id} item={p} searchId={id}
                onReact={setReaction} />)}
            </div>
          )}
        </>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="glass border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Sélectionne tes productions</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400 -mt-1">Maximum {remaining} production(s) — <span className="text-violet-300 font-mono">{picked.length}/{remaining}</span></p>
          <div className="max-h-[50vh] overflow-y-auto space-y-2 mt-2 pr-1">
            {prods.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-zinc-400 mb-3">Tu n'as pas encore de production.</p>
                <button onClick={() => nav("/app/productions")} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-sm font-medium">Uploader une prod</button>
              </div>
            ) : prods.map((p) => (
              <ProductionPickRow key={p.id} prod={p} selected={picked.includes(p.id)} onToggle={togglePick} disabled={picked.length >= remaining} />
            ))}
          </div>
          {prods.length > 0 && (
            <button onClick={submitProposals} disabled={picked.length === 0 || busy} data-testid="proposal-upload-submit-button"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold transition-all disabled:opacity-50 mt-2">
              {busy && <Loader2 size={16} className="animate-spin" />} ENVOYER {picked.length > 0 ? `MES ${picked.length}` : "MES"} PRODUCTION{picked.length > 1 ? "S" : ""}
            </button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
